<template>
	<div class="app-top-bar d-flex justify-center">
		<v-app-bar
			class="justify-space-between"
			clipped-right
			:height="headerHeight"
			:max-width="appBarMaxWidth"
		>
			<v-row class="app-top-bar__row">
				<v-col
					:cols="leftColumnSize"
					class="app-top-bar__column-left"
					v-if="!isTabletOrMobile || !isSearchEnabled"
				>
					<v-btn
						class="app-top-bar__mobile-menu-btn pa-2 rounded-xl"
						v-if="showMobileMenuButton && !isMobileSearchEnabled"
						@click="mobileMenuClick"
					>
						<v-icon> mdi-menu</v-icon>
					</v-btn>
					<router-link :to="getLogoUrl" v-if="!isTabletOrMobile">
						<v-toolbar-title class="left-side">
							<img :src="headerLogo" alt="Logo" class="logo" />
							<span class="title-text">{{ appTitle }}</span>
						</v-toolbar-title>
					</router-link>
				</v-col>
				<v-col
					v-if="isTabletOrMobile && !isMobileSearchEnabled"
					class="d-flex align-center justify-center"
				>
					<router-link :to="getLogoUrl" v-if="!isMobileSearchEnabled">
						<v-toolbar-title class="left-side">
							<img :src="headerLogo" alt="Logo" class="logo" />
							<span class="title-text">{{ appTitle }}</span>
						</v-toolbar-title>
					</router-link>
				</v-col>
				<v-col
					:cols="rightColumnSize"
					:style="[
						showOnThisPage && isSearchEnabled
							? { 'justify-content': 'space-between' }
							: { 'justify-content': 'flex-end' },
					]"
					class="app-top-bar__column-right"
				>
					<v-btn
						class="app-top-bar__search-button-close elevation-0"
						v-if="isSearchEnabled && isMobileSearchEnabled"
						@click="toggleMobileSearch"
						style="min-width: auto"
					>
						<Icon type="chevron_left" color="#fff" :size="18" />
					</v-btn>
					<AppSearch v-if="showOnThisPage && isSearchEnabled" />

					<Menu
						:showOnThisPage="showOnThisPage"
						v-if="!isMobileSearchEnabled"
						:is-auth="isAuth"
						:opacity-routes="opacityRoutes"
						:profile-settings="menuItems"
						:categories="categoriesList"
						:user-avatar="userProfileAvatar"
						@toggleMobileSearch="toggleMobileSearch"
					/>
				</v-col>
			</v-row>
		</v-app-bar>
		<MobileMenu
			:display-menu="drawer"
			:is-auth="isAuth"
			:profile-settings="menuItems"
			:categories="categoriesList"
			:user-avatar="userProfileAvatar"
			@closeMenu="toggleMobileMenu"
		/>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import MainLogo from '@/assets/images/logo.png'
import profileIcon from '@/assets/images/profile-icon.svg'
import settingsIcon from '@/assets/images/settings-icon.svg'
import logoutIcon from '@/assets/images/logout-icon.svg'

import MobileMenu from '@/components/MobileMenu'
import Menu from '@/components/Menu'
import Dwh from '@/mixins/Dwh'
import AppSearch from '@/components/Layouts/parts/AppSearch.vue'

export default {
	name: 'TopBar',
	mixins: [Dwh],
	components: {
		MobileMenu,
		Menu,
		AppSearch,
	},
	data() {
		return {
			MainLogo: MainLogo,
			selectedCategory: null,
			navOpacity: {
				left: false,
			},
			drawer: false,
			category: null,
			navOp: false,
			isActive: false,
			mobileSearch: false,
			opacityRoutes: ['CreatePost', 'chat', 'Notifications', 'MapPage'],
		}
	},
	computed: {
		...mapGetters('category', ['isCategoryLoading', 'categories']),
		...mapGetters('auth', ['isAuthenticated', 'profile']),
		...mapGetters('settings', ['appCore', 'ci_cd', 'baseCore']),

		showOnThisPage() {
			const routesWithSearch = [
				'events',
				'users',
				'directory',
				'forums',
				'threads',
				'resources',
				'resource',
			]
			return routesWithSearch.includes(this.$route.name)
		},

		categoriesList: function () {
			return this.categories
		},
		isAuth: function () {
			return this.isAuthenticated
		},
		userProfileAvatar: function () {
			return this.profile?.user_logo
		},
		headerHeight() {
			return this.$vuetify.breakpoint.width < 768 ? '56px' : '60px'
		},
		headerLogo() {
			if (this.ci_cd) {
				return this.ci_cd.app_icon
			}

			return MainLogo
		},
		appTitle() {
			if (this.ci_cd) {
				return this.ci_cd.device_name
			}

			return 'Clowder Web'
		},
		getLogoUrl() {
			if (this.appCore) {
				const { route = { name: 'events' } } =
					this.appCore.appMenuNormalized.baseMenuItems[0]
				return route
			}

			return { name: 'events' }
		},
		userIsMember() {
			return this.profile.is_member
		},
		menuItems() {
			const items = [
				{
					id: 1,
					name: 'Profile',
					action: () => {
						this.sendTrigger({
							category: 'Navigation',
							action: 'menu_profile',
							module: 'Menu',
							title: 'Profile',
						})
					},
					path: '/profile-pre-view',
					icon: profileIcon,
				},
				{
					id: 2,
					name: 'Settings',
					action: () => {
						this.sendTrigger({
							category: 'Navigation',
							action: 'menu_settings',
							module: 'Menu',
							title: 'Settings',
						})
					},
					path: '/settings',
					icon: settingsIcon,
				},
				{ id: 3, name: 'Logout', action: this.exit, icon: logoutIcon },
			]
			let result = null
			if (!this.appCore || !this.baseCore) {
				return null
			}
			const pattern = /\/\/(.+)/
			const profileRoute = this.appCore.app_menu.find((item) => {
				const matches = item.url.match(pattern)
				return matches[1] === 'profile'
			})
			const settingsRoute = this.appCore.app_menu.find((item) => {
				const matches = item.url.match(pattern)
				return matches[1] === 'settings'
			})
			if (settingsRoute) {
				const role = settingsRoute.role
				if (
					(role !== null &&
						((!role && this.userIsMember) || (role && !this.userIsMember)) &&
						settingsRoute.block === 1) ||
					(!this.baseCore.privacy_policy &&
						!this.baseCore.terms_of_use &&
						!this.baseCore.contact_us_url)
				) {
					result = items.filter((item) => item.id !== 2)
				}
			}
			if (profileRoute) {
				const role = profileRoute.role
				if (
					role !== null &&
					((!role && this.userIsMember) || (role && !this.userIsMember)) &&
					profileRoute.block === 1
				) {
					result = result
						? result.filter((item) => item.id !== 1)
						: items.filter((item) => item.id !== 1)
				}
			}
			return result || items
		},
		showMobileMenuButton() {
			return this.$vuetify.breakpoint.width < 920
		},
		isTabletOrMobile() {
			return this.$vuetify.breakpoint.width < 769
		},
		isMobileSearchEnabled() {
			return this.isTabletOrMobile && this.mobileSearch
		},
		isSearchEnabled() {
			return !this.isTabletOrMobile || this.mobileSearch
		},
		appBarMaxWidth() {
			const displayWidth = this.$vuetify.breakpoint.width
			return displayWidth >= 1160 ? '1160px' : displayWidth >= 920 ? '880px' : '600px'
		},
		leftColumnSize() {
			return this.$vuetify.breakpoint.width >= 1160 ? 3 : null
		},
		rightColumnSize() {
			return this.$vuetify.breakpoint.width >= 1160 ? 9 : null
		},
	},
	watch: {
		selectedCategory: {
			handler(val) {
				this.categoryId = val ? this.categories[val].id : null
				this.categoryHandler(this.categoryId)
			},
			deep: true,
		},
		$route() {
			this.navOpacity.left = this.opacityRoutes.includes(this.$route.name)
		},
	},
	methods: {
		...mapActions('category', ['fetchCategories', 'setCategory']),
		...mapActions('auth', ['logout']),
		async exit() {
			this.logout()
		},
		categoryHandler(id) {
			if (this.$route.name !== 'Home') {
				this.$router.push({ name: 'Home' })
			}

			this.setCategory(id)

			this.$emit('nav-change', {
				category: id,
			})
		},
		toggleMobileMenu(value) {
			this.drawer = value
		},
		mobileMenuClick() {
			this.sendTrigger({
				category: 'Navigation',
				action: 'menu_click',
				module: 'Menu',
			})
			this.toggleMobileMenu(true)
		},
		toggleMobileSearch() {
			this.mobileSearch = !this.mobileSearch
		},
	},
}
</script>

<style lang="scss">
.logo {
	height: 24px;
	margin-right: 8px;
}

.left-side {
	display: flex;
	align-items: center;
	cursor: pointer;
}

.title-text {
	height: 20px;
	color: rgb(255, 255, 255);
	font-size: 16px;
	font-family: Montserrat, sans-serif;
	font-weight: 600;
	line-height: 20px;
}

.v-list-item--active::before {
	opacity: 0 !important;
}

.logo-opacity {
	transition: ease-in 0.2s;
	opacity: 0.5;
}

.app-top-bar {
	box-shadow: 0 4px 11px rgb(0 0 0 / 6%) !important;
	z-index: 1;
	background-color: var(--accent-color);

	&__row {
		height: 60px;
		flex-wrap: nowrap;
	}

	&__search-button-close {
		background-color: unset !important;
	}

	&__column {
		&-left,
		&-right {
			height: 100%;
			display: flex;
			align-items: center;
			gap: 12px;
			padding: 0;
		}

		&-left {
			width: 280px !important;
			max-width: 280px;

			@media (max-width: 920px) {
				width: auto !important;
				max-width: none;
				justify-content: flex-start;
			}
		}

		&-right {
			@media (max-width: 768px) {
				justify-content: flex-end;
			}
		}
	}

	a {
		text-decoration: none;
	}

	header {
		margin: 0 auto;
		box-shadow: none !important;

		@media (max-width: 620px) {
			margin: 0 calc(10px + (20 - 10) * (620px - 100vw) / (620 - 600));
		}

		@media (max-width: 600px) {
			margin: 0 20px;
		}
	}

	&__active-profile {
		color: #747474 !important;
		text-decoration: underline;
	}

	&__mobile-menu-btn {
		box-shadow: none !important;
		height: 32px !important;
		width: 32px !important;
		min-width: auto !important;
		background-color: var(--accent-color) !important;
		color: #fff !important;
	}

	.map-icon {
		svg {
			stroke: #b9b9b9;
			fill: none;
		}
	}

	svg {
		fill: #b9b9b9;
	}

	.create-button-wrapper {
		display: flex;
		border: 2px solid #aaa;
		border-radius: 50%;
		padding: 10px;
		background-color: #fff !important;

		svg {
			fill: #aaa;
		}
	}

	&__navigation {
		&-active {
			opacity: 1;

			svg {
				fill: #db7842;
			}

			.create-button-wrapper {
				color: #fff !important;
				border: 2px solid #db7842;
				background-color: #db7842 !important;

				svg {
					fill: #fff;
				}
			}
		}

		&-active.map-icon {
			svg {
				stroke: #db7842;
				fill: none !important;
			}
		}
	}

	&__right-menu {
		display: flex;
		justify-content: flex-end;
		align-items: center;

		.theme--light.v-list-item:hover::before {
			opacity: 0;
		}

		.theme--light.v-btn:hover::before {
			opacity: 0;
		}

		.v-ripple__container {
			opacity: 0;
		}
	}

	&__categories {
		cursor: pointer;
		height: 100%;
		margin-right: 16%;
		text-transform: none !important;
		font: bolder 15px/1.2 'Montserrat' !important;
		text-decoration: none !important;
	}

	&__category {
		color: #fff !important;
		font: 600 13px/1.45 'Montserrat';
		margin-left: 15px;
		text-decoration: none;
		text-transform: none !important;
		background-color: inherit !important;
		border-left-width: 1px !important;
		height: 36px !important;
		min-width: 64px;
		padding: 0 16px !important;

		.v-btn__content {
			font-family: Montserrat, sans-serif;
			font-style: normal;
			font-weight: bold;
			font-size: 15px;
			line-height: 18px;
			letter-spacing: 0.225472px;
			text-transform: none !important;
			color: rgba(32, 34, 50, 1);
		}

		&:before {
			opacity: 0 !important;
		}
	}

	&__category-active {
		background-color: #db7842 !important;
		opacity: 1 !important;

		.v-btn--active {
			opacity: 0 !important;
		}

		.v-btn__content {
			color: #fff;
		}

		&:before {
			opacity: 0 !important;
		}
	}

	.v-toolbar {
		&__content {
			padding-bottom: 0;
			padding-top: 0;
			background-color: var(--accent-color);
			justify-content: space-between;
			column-gap: 16px;
		}
	}

	&__category:hover {
		.v-btn__content {
			color: #fff !important;
		}

		background: #db7842 !important;
		transition: color 0.2s, background-color 0.2s, border-color 0.2s;
	}

	&__category:hover::before {
		opacity: 0 !important;
	}

	&__login {
		color: #fff !important;
		height: 36px;
		font: 600 13px/1.45 'Montserrat';
		margin-left: 15px;
		text-decoration: none;
		text-transform: none !important;
	}

	&__map-icon {
		padding-left: 3px;
	}

	&__notification-icon {
		padding-right: 5px !important;
	}

	&__avatar-btn {
		padding-bottom: 3px !important;
		padding-right: 4px !important;
	}

	.theme--light.v-btn:focus::before {
		opacity: 0;
	}

	&__profile-avatar {
		padding-right: 2px;
		padding-top: 2px;

		.v-btn:before {
			opacity: 0 !important;
		}

		.v-ripple__container {
			opacity: 0 !important;
		}
	}

	&__profile-menu {
		margin-top: 2px;
		contain: initial !important;
		overflow: visible !important;
	}

	&__profile-card {
		border-radius: 4px !important;
		width: 190px !important;

		.theme--light.v-list-item:not(.v-list-item--active):not(.v-list-item--disabled) {
			color: #747474 !important;
		}

		&-item {
			font-family: Montserrat, sans-serif;
			font-size: 16px;

			&:hover {
				text-decoration: underline;
			}
		}
	}

	@media screen and (min-width: 1320px) {
		.v-toolbar__content {
			max-width: 1284px;
			margin: 0 auto;
		}
	}

	@media screen and (min-width: 1400px) {
		.v-toolbar__content {
			max-width: 1400px;
			margin: 0 auto;
		}
	}

	.v-app-bar {
		background-color: var(--accent-color) !important;

		&__nav-icon .v-icon {
			font-size: 45px !important;
		}
	}
}
</style>
